/**
 * Native Push Notifications via Capacitor
 * Handles APNs (iOS) and FCM (Android) token registration,
 * notification tap deep linking, token cleanup, and pending deep link store.
 *
 * ANDROID PRODUCTION REQUIREMENT:
 *   google-services.json must be placed at android/app/google-services.json
 *   for FCM to generate push tokens. Without it, registration will fail
 *   with a 'registrationError' event. See android/app/build.gradle for
 *   full setup instructions.
 *
 * iOS PRODUCTION REQUIREMENT:
 *   Push notification entitlement must be enabled in Xcode, and a valid
 *   APNs key must be uploaded to Firebase (or your push provider).
 */
import { Capacitor } from '@capacitor/core';
import { captureError } from "@/lib/observability";
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { supabase } from '@/integrations/supabase/client';

const PENDING_DEEP_LINK_KEY = 'tm_pending_deep_link';
const LAST_PUSH_TOKEN_KEY = 'tm_last_push_token';
const PUSH_TOKEN_UPDATED_KEY = 'tm_push_token_updated';
const PUSH_PERMISSION_KEY = 'tm_push_permission';

/** Check if native push is available (real device, not web) */
export function isNativePushAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

/** Store a pending deep link (for when user taps push but isn't logged in) */
export function setPendingDeepLink(path: string) {
  try {
    localStorage.setItem(PENDING_DEEP_LINK_KEY, path);
  } catch {}
}

/** Consume and clear the pending deep link */
export function consumePendingDeepLink(): string | null {
  try {
    const link = localStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (link) localStorage.removeItem(PENDING_DEEP_LINK_KEY);
    return link;
  } catch {
    return null;
  }
}

/** Get stored push notification status for debug UI */
export function getPushDebugInfo(): {
  lastToken: string | null;
  tokenUpdatedAt: string | null;
  permissionStatus: string | null;
  platform: string;
  isNative: boolean;
} {
  return {
    lastToken: localStorage.getItem(LAST_PUSH_TOKEN_KEY),
    tokenUpdatedAt: localStorage.getItem(PUSH_TOKEN_UPDATED_KEY),
    permissionStatus: localStorage.getItem(PUSH_PERMISSION_KEY),
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
  };
}

/**
 * Request push permission and register the device token with backend.
 * Call this after the user logs in or on dashboard load.
 */
export async function registerNativePush(): Promise<boolean> {
  if (!isNativePushAvailable()) {
    console.log('[NativePush] Not on native platform, skipping');
    return false;
  }

  const platform = Capacitor.getPlatform();
  console.log(`[NativePush][DIAG] ===== REGISTRATION START (platform: ${platform}) =====`);

  try {
    // Step 1: Check permissions
    console.log('[NativePush][DIAG] Step 1: Checking current permissions...');
    let permStatus: Awaited<ReturnType<typeof PushNotifications.checkPermissions>>;
    try {
      permStatus = await PushNotifications.checkPermissions();
      console.log(`[NativePush][DIAG] Step 1 result: ${permStatus.receive}`);
    } catch (permCheckErr) {
      console.error('[NativePush][DIAG] Step 1 FAILED (checkPermissions threw):', JSON.stringify(permCheckErr));
      return false;
    }
    localStorage.setItem(PUSH_PERMISSION_KEY, permStatus.receive);

    // Step 2: Request permissions if needed
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      console.log('[NativePush][DIAG] Step 2: Requesting permissions...');
      try {
        permStatus = await PushNotifications.requestPermissions();
        console.log(`[NativePush][DIAG] Step 2 result: ${permStatus.receive}`);
      } catch (permReqErr) {
        console.error('[NativePush][DIAG] Step 2 FAILED (requestPermissions threw):', JSON.stringify(permReqErr));
        return false;
      }
      localStorage.setItem(PUSH_PERMISSION_KEY, permStatus.receive);
    } else {
      console.log(`[NativePush][DIAG] Step 2: Skipped (already ${permStatus.receive})`);
    }

    if (permStatus.receive !== 'granted') {
      console.log(`[NativePush][DIAG] ABORTED: Permission not granted (${permStatus.receive})`);
      return false;
    }

    // Step 3: Call PushNotifications.register()
    console.log('[NativePush][DIAG] Step 3: Calling PushNotifications.register()...');
    try {
      await PushNotifications.register();
      console.log('[NativePush][DIAG] Step 3 completed: register() resolved. Now waiting for registration/registrationError event callback.');
    } catch (registerErr) {
      console.error('[NativePush][DIAG] Step 3 FAILED (register() threw):', JSON.stringify(registerErr));
      return false;
    }

    return true;
  } catch (error) {
    console.error('[NativePush][DIAG] UNEXPECTED ERROR in registerNativePush:', JSON.stringify(error), error);
    captureError('push.registration', error);
    return false;
  }
}

/**
 * Send device token to backend for storage.
 * Called internally when the 'registration' event fires.
 */
async function sendTokenToBackend(token: string): Promise<boolean> {
  const platform = Capacitor.getPlatform(); // 'ios' | 'android'
  console.log(`[NativePush][DIAG] Step 4: sendTokenToBackend called (platform: ${platform})`);
  console.log(`[NativePush][DIAG] Token prefix: ${token.substring(0, 20)}... (length: ${token.length})`);

  try {
    // Check if user has an active session before calling the edge function
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      console.error('[NativePush][DIAG] Step 4 FAILED: No active auth session — cannot call register-device-token');
      return false;
    }
    console.log(`[NativePush][DIAG] Step 4: Auth session found for user ${sessionData.session.user.id.substring(0, 8)}...`);

    const { data, error } = await supabase.functions.invoke('register-device-token', {
      body: { token, platform },
    });

    if (error) {
      console.error('[NativePush][DIAG] Step 4 FAILED: Edge function returned error:', JSON.stringify(error));
      return false;
    }

    // Store locally for debug UI
    localStorage.setItem(LAST_PUSH_TOKEN_KEY, token.substring(0, 30) + '...');
    localStorage.setItem(PUSH_TOKEN_UPDATED_KEY, new Date().toISOString());

    console.log('[NativePush][DIAG] Step 4 SUCCESS: Token registered. Response:', JSON.stringify(data));
    return true;
  } catch (err) {
    console.error('[NativePush][DIAG] Step 4 UNEXPECTED ERROR:', JSON.stringify(err), err);
    return false;
  }
}

/**
 * Unregister the current device's push token on logout.
 * Removes the token from the backend so notifications stop.
 */
export async function unregisterPushToken(): Promise<void> {
  if (!isNativePushAvailable()) return;

  try {
    console.log('[NativePush] Unregistering push token on logout...');

    // Remove all device tokens for this user on this platform
    const platform = Capacitor.getPlatform();
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user?.id) {
      // Call edge function to clean up (uses service role to bypass RLS)
      await supabase.functions.invoke('register-device-token', {
        body: { token: '__LOGOUT__', platform, action: 'unregister' },
      });
      console.log('[NativePush] Token unregistered');
    }

    // Clear local state
    localStorage.removeItem(LAST_PUSH_TOKEN_KEY);
    localStorage.removeItem(PUSH_TOKEN_UPDATED_KEY);
  } catch (err) {
    console.warn('[NativePush] Failed to unregister token (non-fatal):', err);
  }
}

/**
 * Initialize all push notification listeners.
 * Call once at app startup (inside a component with access to navigate).
 */
export function initNativePushListeners(
  onNavigate: (path: string) => void
): () => void {
  if (!isNativePushAvailable()) return () => {};

  const listeners: Array<{ remove: () => void }> = [];

  // Token received → send to backend
  PushNotifications.addListener('registration', async (token) => {
    console.log('[NativePush][DIAG] ===== REGISTRATION EVENT FIRED =====');
    console.log(`[NativePush][DIAG] Token received: ${token.value ? token.value.substring(0, 20) + '...' : 'NULL/EMPTY'}`);
    console.log(`[NativePush][DIAG] Token length: ${token.value?.length || 0}`);
    console.log(`[NativePush][DIAG] Platform: ${Capacitor.getPlatform()}`);
    if (!token.value) {
      console.error('[NativePush][DIAG] Token value is null/empty — cannot register with backend');
      return;
    }
    await sendTokenToBackend(token.value);
  }).then(h => listeners.push(h));

  // Registration error — most common cause on Android: missing google-services.json
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[NativePush][DIAG] ===== REGISTRATION ERROR EVENT FIRED =====');
    console.error('[NativePush][DIAG] Error details:', JSON.stringify(error));
    console.error(`[NativePush][DIAG] Platform: ${Capacitor.getPlatform()}`);
    if (Capacitor.getPlatform() === 'android') {
      console.error(
        '[NativePush][DIAG] ⚠️  ANDROID PUSH SETUP: If you see "MISSING_INSTANCEID_SERVICE" or ' +
        '"SERVICE_NOT_AVAILABLE", ensure google-services.json is placed at android/app/google-services.json ' +
        'and rebuild with: npm run build && npx cap sync android'
      );
    }
    localStorage.setItem(PUSH_PERMISSION_KEY, 'error: ' + (error?.error || 'unknown'));
  }).then(h => listeners.push(h));

  // Notification received while app is in foreground → show local notification
  PushNotifications.addListener('pushNotificationReceived', async (notification) => {
    console.log('[NativePush] Foreground notification:', JSON.stringify({
      title: notification.title,
      body: notification.body,
      data: notification.data,
    }));
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            title: notification.title || 'TransportMate',
            body: notification.body || '',
            id: Date.now(),
            extra: notification.data,
            sound: 'default',
            smallIcon: 'ic_notification',
          },
        ],
      });
      console.log('[NativePush] Local notification scheduled for foreground push');
    } catch (err) {
      console.warn('[NativePush] Failed to show local notification:', err);
    }
  }).then(h => listeners.push(h));

  const resolveNotificationTarget = (rawData: Record<string, unknown>): string | null => {
    const type = typeof rawData.type === 'string' ? rawData.type : null;

    if (
      rawData.job_id ||
      type === 'job_assigned' ||
      type === 'job_updated' ||
      type === 'collection_reminder'
    ) {
      return '/dashboard';
    }

    return null;
  };

  // Notification tapped (from background or killed state)
  PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
    console.log('[NativePush] Notification tapped:', JSON.stringify(action.notification.data));

    const data = (action.notification.data || {}) as Record<string, unknown>;
    const targetPath = resolveNotificationTarget(data);

    if (!targetPath) return;

    // Check if user is authenticated
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      // Logged in → navigate directly
      onNavigate(targetPath);
    } else {
      // Not logged in → store deep link, login flow will pick it up
      setPendingDeepLink(targetPath);
      onNavigate('/auth');
    }
  }).then(h => listeners.push(h));

  // Also listen for local notification taps (foreground re-shown notifications)
  LocalNotifications.addListener('localNotificationActionPerformed', async (action) => {
    console.log('[NativePush] Local notification tapped:', JSON.stringify(action.notification.extra));

    const data = (action.notification.extra || {}) as Record<string, unknown>;
    const targetPath = resolveNotificationTarget(data);

    if (!targetPath) return;

    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      onNavigate(targetPath);
    } else {
      setPendingDeepLink(targetPath);
      onNavigate('/auth');
    }
  }).then(h => listeners.push(h));

  return () => {
    listeners.forEach(h => h.remove());
  };
}

/** Clear the iOS app badge count */
export async function clearAppBadge(): Promise<void> {
  if (!isNativePushAvailable()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
    // On iOS, setting badge to 0 via a local notification clears the badge
    await LocalNotifications.schedule({
      notifications: [{
        id: 99999,
        title: '',
        body: '',
        schedule: { at: new Date(Date.now() + 100) },
        extra: { clearBadge: true },
      }],
    });
    // Cancel immediately - the schedule triggers badge clear
    setTimeout(async () => {
      try {
        await LocalNotifications.cancel({ notifications: [{ id: 99999 }] });
      } catch {}
    }, 200);
  } catch (err) {
    console.warn('[NativePush] Failed to clear badge:', err);
  }
}
